/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { customerLogin, fetchCustomerMe } from "@/lib/api";

// Separate storage key and context from useAdminAuth — a customer session and an
// admin session are two completely different token namespaces (see billing_routes.py's
// _require_customer, which rejects any token without kind:"customer"), so there is no
// path by which logging into one ever grants the other.
const STORAGE_KEY = "mysitecare-customer-token";
const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => window.localStorage.getItem(STORAGE_KEY) || null);
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setChecking(false);
      return undefined;
    }
    fetchCustomerMe(token)
      .then((me) => { if (!cancelled) { setUser(me.user); setCustomer(me.customer); } })
      .catch(() => {
        if (!cancelled) {
          setToken(null);
          setUser(null);
          setCustomer(null);
          window.localStorage.removeItem(STORAGE_KEY);
        }
      })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await customerLogin(email, password);
    window.localStorage.setItem(STORAGE_KEY, res.token);
    setChecking(true);
    setToken(res.token);
    return res;
  }, []);

  // Used by the shared /register page (registration returns a ready-to-use token
  // directly — no separate login round-trip needed), mirroring useAdminAuth's own
  // setSession.
  const setSession = useCallback((sessionToken) => {
    window.localStorage.setItem(STORAGE_KEY, sessionToken);
    setChecking(true);
    setToken(sessionToken);
  }, []);

  const logout = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
    setCustomer(null);
    setChecking(false);
  }, []);

  const isLoggedIn = Boolean(token && user);
  const value = useMemo(() => ({
    token, user, customer, checking, isLoggedIn, login, logout, setSession,
  }), [token, user, customer, checking, isLoggedIn, login, logout, setSession]);

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
  return ctx;
}
